import React, { useState } from 'react';

import { Utils } from '@adobe/aem-react-editable-components';
import { ApolloClient, ApolloLink, ApolloProvider, HttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { useAuth } from 'context/Auth';
import PropTypes from 'prop-types';

import { ActionButton } from 'components/ActionButton/ActionButton';
import { CustomModal } from 'components/CustomModal';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';

import { isTokenExpired, loginWithRedirect, logoutWithRedirect } from 'utils/AuthUtils';
import { getTenantId } from 'utils/tenantUtil';

// Enum for popup types
const POPUP_TYPE = {
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  USER_NOT_FOUND: 'USER_NOT_FOUND',
};

// Config for different popups
const POPUP_CONFIG = {
  [POPUP_TYPE.SESSION_EXPIRED]: {
    title: 'Login timeout.',
    content: 'Bitte loggen Sie sich erneut ein. Ihre Session ist abgelaufen.',
  },
  [POPUP_TYPE.USER_NOT_FOUND]: {
    title: 'Nutzer nicht gefunden',
    content:
      'Ihr Fachkraft-Konto ist nicht mehr verfügbar. Bitte melden Sie sich erneut an oder wenden Sie sich an Ihren Netzbetreiber.',
  },
};

export const Apollo = ({ children, isPublic }) => {
  const { token, isLoading, isCIAM } = useAuth();
  const [popupType, setPopupType] = useState(null);

  const tenantId = getTenantId();
  const isEditor = Utils.isInEditor();

  let uri;
  if (process.env.REACT_APP_GRAPHQL_ENDPOINT) {
    uri = `${process.env.REACT_APP_GRAPHQL_ENDPOINT}?tenant=${tenantId}`;
  }

  if (!uri) {
    // if this happens the env vars are not set correctly
    return <div>no uri</div>;
  }

  if (isLoading) {
    return <LoadingIndicator />;
  }
  const jwtValidation = setContext((_, { headers }) => {
    if ((!token || isTokenExpired(token)) && !isPublic && !isEditor) {
      setPopupType(POPUP_TYPE.SESSION_EXPIRED);
      throw new Error('Invalid or missing token. Request cancelled.');
    }
    return {
      headers,
    };
  });

  // Error handling link
  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    if (graphQLErrors && operation.operationName !== 'getInstaller') {
      graphQLErrors.forEach(({ message, extensions }) => {
        if (extensions?.code === 'INTERNAL_SERVER_ERROR' && message === 'User not found') {
          setPopupType(POPUP_TYPE.USER_NOT_FOUND);
        }
      });
    }

    if (networkError) {
      console.error(`[Network error]: ${networkError}`);
    }
  });

  const cache = new InMemoryCache();
  const httpLink = new HttpLink({
    uri,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : []),
    },
  });
  const link = ApolloLink.from([jwtValidation, errorLink, httpLink]);

  const client = new ApolloClient({
    cache,
    link,
  });
  return (
    <ApolloProvider client={client}>
      <>
        {children}

        <CustomModal
          isOpen={!!popupType && !isEditor}
          title={POPUP_CONFIG[popupType]?.title ?? ''}
          ctaButtons={
            <ActionButton
              onClick={() => {
                setPopupType(null);
                if (popupType === POPUP_TYPE.USER_NOT_FOUND) {
                  logoutWithRedirect(isCIAM);
                  return;
                }
                loginWithRedirect(isCIAM);
              }}
              title="Ok"
            />
          }
          content={POPUP_CONFIG[popupType]?.content ?? ''}
        />
      </>
    </ApolloProvider>
  );
};

export const PUBLIC_NO_AUTH_CONFIG = {
  context: {
    headers: {
      Authorization: '',
    },
  },
};

Apollo.propTypes = {
  children: PropTypes.node.isRequired,
  isPublic: PropTypes.bool,
};

Apollo.defaultProps = {
  isPublic: false,
};
